import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import type { ReactNode } from 'react';
import { cn } from './lib/cn';

export interface TooltipProviderProps {
  children: ReactNode;
  /** Delay before the first tooltip in a group appears. */
  delayDuration?: number;
}

/** Mount once, near the app root. */
export function TooltipProvider({ children, delayDuration = 200 }: TooltipProviderProps) {
  return (
    <TooltipPrimitive.Provider delayDuration={delayDuration} skipDelayDuration={300}>
      {children}
    </TooltipPrimitive.Provider>
  );
}

export interface TooltipProps {
  /** The trigger element. Must accept a ref (use a real element or forwardRef). */
  children: ReactNode;
  content: ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
  /** Optional secondary line, e.g. a keyboard shortcut. */
  shortcut?: string;
  /** Disable without unmounting the trigger. */
  disabled?: boolean;
  sideOffset?: number;
  className?: string;
}

export function Tooltip({
  children, content, side = 'top', align = 'center',
  shortcut, disabled = false, sideOffset = 6, className,
}: TooltipProps) {
  if (disabled) return <>{children}</>;

  return (
    <TooltipPrimitive.Root>
      <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          side={side}
          align={align}
          sideOffset={sideOffset}
          className={cn(
            'z-tooltip max-w-64 rounded-md bg-foreground px-2.5 py-1.5 text-base font-medium text-background shadow-dropdown',
            'animate-scale-in data-[state=closed]:animate-fade-in',
            className,
          )}
        >
          {content}
          {shortcut ? (
            <span className="ml-2 rounded-xs bg-background/20 px-1 py-0.5 text-3xs font-semibold tabular">
              {shortcut}
            </span>
          ) : null}
          <TooltipPrimitive.Arrow className="fill-foreground" width={10} height={5} />
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
}
